import React, { useState, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { AspectRatio, Box, Flex, Icon, Image } from "@chakra-ui/react";
import SocialAccountPropType from "../../../_types//SocialAccount";
import PostPropType from "../../../_types/Post";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";
import { getFixedPhotosVideosOrder } from "../PostEditor/helpers";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const SocialPostCardInstagramStory = ({ account, post, time, ...rest }) => {
  const listRef = useRef(null);
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(0);
  const [hover, setHover] = useState(false);
  const type = account?.social_type;

  const mediaItems = useMemo(() => getFixedPhotosVideosOrder(post?.photos, post?.videos).items, [post?.photos, post?.videos]);

  const stopVideos = useCallback(() => {
    listRef.current?.querySelectorAll("video")?.forEach((item) => {
      item.pause();
      item.currentTime = 0;
    });
  }, [listRef]);

  const handleCarouselPrevClick = useCallback(() => {
    stopVideos();
    setCarouselActiveIndex(carouselActiveIndex - 1);
  }, [carouselActiveIndex, setCarouselActiveIndex, stopVideos]);

  const handleCarouselNextClick = useCallback(() => {
    stopVideos();
    setCarouselActiveIndex(carouselActiveIndex + 1);
  }, [carouselActiveIndex, setCarouselActiveIndex, stopVideos]);

  const handleMouseEnter = useCallback(() => setHover(true), [setHover]);

  const handleMouseLeave = useCallback(() => setHover(false), [setHover]);

  if (!account || !post || !type) {
    return null;
  };

  const renderAvatar = () => (
    <Box
      sx={{
        position: "relative",
        width: "26px",
        height: "26px",
        lineHeight: "26px",
      }}
    >
      <SocialAccountAvatar
        sx={{
          width: "full",
          height: "full",
          fontSize: "16px",
          borderRadius: "full",
        }}
        name={account.name}
        src={account.image}
        socialType={account.social_type}
      />
    </Box>
  );

  const renderHeader = () => (
    <Flex
      sx={{
        flexDirection: "column",
        position: "absolute",
        top: "0",
        width: "100%",
        padding: "10px 10px 25px",
        background: "linear-gradient(180deg,rgba(38,38,38,.8) 0%,rgba(38,38,38,0) 100%)",
        pointerEvents: "none",
        transition: "opacity 150ms ease-in-out",
        opacity: hover ? "0" : "1",
      }}
    >
      <Flex sx={{ marginX: "-1px", height: "2px" }}>
        {mediaItems.map((item, index) => (
          <Box
            sx={{
              flex: "1",
              height: "100%",
              marginX: "1px",
              borderRadius: "1px",
              background: "#fff",
              transition: "opacity .2s ease-in-out",
              opacity: carouselActiveIndex >= index ? "1" : "0.35",
            }}
          />
        ))}
      </Flex>
      <Flex sx={{ alignItems: "center", marginTop: "10px" }}>
        {renderAvatar()}
        <Box
          sx={{
            fontSize: "14px",
            color: "#fff",
            marginLeft: "8px",
          }}
        >
          {account.name}
        </Box>
        <Box
          sx={{
            fontSize: "12px",
            color: "#fff",
            opacity: "0.6",
            marginLeft: "10px",
          }}
        >
          {time}
        </Box>
        {/* <Box sx={{ marginLeft: "auto", fontSize: "14px" }}>
          <FaEllipsisV />
        </Box> */}
      </Flex>
    </Flex>
  );

  const renderFooter = () => (
    <Flex
      sx={{
        position: "absolute",
        bottom: "0",
        width: "100%",
        height: "17%",
        flexDirection: "column",
        justifyContent: "flex-end",
        padding: "0 10px 10px",
        background: "linear-gradient(180deg,rgba(38,38,38,0) 0%,rgba(38,38,38,.6) 100%)",
        pointerEvents: "none",
        transition: "opacity 150ms ease-in-out",
        opacity: hover ? "0" : "1",
      }}
    >
      <Flex sx={{ alignItems: "center", height: "36px" }}>
        <Box
          sx={{
            flex: "1",
            border: "1px solid",
            borderColor: "#dbdbdb",
            borderRadius: "100vh",
            height: "100%",
            marginRight: "5px",
          }}
        />
        <Box sx={{ fontSize: "20px", padding: "5px", color: "#dbdbdb" }}>
          <Icon viewBox="0 0 24 24" fill="currentcolor">
            <path d="M16.792 3.904A4.989 4.989 0 0121.5 9.122c0 3.072-2.652 4.959-5.197 7.222-2.512 2.243-3.865 3.469-4.303 3.752-.477-.309-2.143-1.823-4.303-3.752C5.141 14.072 2.5 12.167 2.5 9.122a4.989 4.989 0 014.708-5.218 4.21 4.21 0 013.675 1.941c.84 1.175.98 1.763 1.12 1.763s.278-.588 1.11-1.766a4.17 4.17 0 013.679-1.938m0-2a6.04 6.04 0 00-4.797 2.127 6.052 6.052 0 00-4.787-2.127A6.985 6.985 0 00.5 9.122c0 3.61 2.55 5.827 5.015 7.97.283.246.569.494.853.747l1.027.918a44.998 44.998 0 003.518 3.018 2 2 0 002.174 0 45.263 45.263 0 003.626-3.115l.922-.824c.293-.26.59-.519.885-.774 2.334-2.025 4.98-4.32 4.98-7.94a6.985 6.985 0 00-6.708-7.218z"></path>
          </Icon>
        </Box>
        <Box sx={{ fontSize: "18px", padding: "5px", color: "#dbdbdb" }}>
          <Icon viewBox="0 0 24 24" fill="currentcolor">
            <line fill="none" stroke="currentColor" strokeLinejoin="round" stroke-width="2" x1="22" x2="9.218" y1="3" y2="10.083"></line>
            <polygon fill="none" points="11.698 20.334 22 3.001 2 3.001 9.218 10.084 11.698 20.334" stroke="currentColor" strokeLinejoin="round" strokeWidth="2"></polygon>
          </Icon>
        </Box>
      </Flex>
    </Flex>
  );

  const renderMedia = () =>  (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Flex
        sx={{
          alignItems: "center",
          justifyContent: "space-around",
          width: `${100 * mediaItems.length}%`,
          height: "100%",
          transform: `translateX(${carouselActiveIndex * (100 / mediaItems.length) * -1}%)`,
          transition: "transform 200ms ease-in-out",
        }}
        ref={listRef}
      >
        {mediaItems.map((item) => (
          <Box key={item.data.url} sx={{ flex: "1", height: "100%" }}>
            {item.field === "videos" ? (
              <Box
                as="video"
                width="100%"
                height="100%"
                src={item.data.url}
                controls={hover}
                preload="metadata"
                poster={item.data.thumbUrl}
              />
            ) : (
              <Image
                width="100%"
                height="100%"
                objectFit="contain"
                src={item.data.url}
              />
            )}
          </Box>
        ))}
      </Flex>
    </Box>
  );

  const carouselFirstItemActive = carouselActiveIndex === 0;
  const carouselLastItemActive =
    carouselActiveIndex === mediaItems.length - 1;

  const carouselNavStyles = {
    position: "absolute",
    top: "50%",
    zIndex: "1",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "20px",
    height: "20px",
    transform: "translateY(-50%)",
    background: "rgba(255, 255, 255, 0.6)",
    borderRadius: "50%",
    color: "rgba(0, 0, 0, 0.6)",
    fontSize: "14px",
    cursor: "pointer",
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: "360px",
        paddingX: "30px",
        fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
      }}
      {...rest}
    >
      <Box>
        <AspectRatio
          ratio={9 / 16}
          sx={{
            overflow: "hidden",
            background: "#000",
            borderRadius: "8px",
          }}
        >
          <Box>
            {renderMedia()}
            {renderHeader()}
            {renderFooter()}
          </Box>
        </AspectRatio>
        <Box
          sx={{
            ...carouselNavStyles,
            left: "5px",
            opacity: carouselFirstItemActive ? "0" : "1",
            visibility: carouselFirstItemActive ? "hidden" : null,
          }}
          onClick={carouselFirstItemActive ? null : handleCarouselPrevClick}
        >
          <Icon as={FaChevronLeft} />
        </Box>
        <Box
          sx={{
            ...carouselNavStyles,
            right: "5px",
            opacity: carouselLastItemActive ? "0" : "1",
            visibility: carouselLastItemActive ? "hidden" : null,
          }}
          onClick={carouselLastItemActive ? null : handleCarouselNextClick}
        >
          <Icon as={FaChevronRight} />
        </Box>
      </Box>
    </Box>
  );
};

SocialPostCardInstagramStory.propTypes = {
  account: SocialAccountPropType.isRequired,
  post: PostPropType.isRequired,
  time: PropTypes.string,
};

export default SocialPostCardInstagramStory;
